import { useRef, useState } from "react";
import {
  CameraIcon,
  ListIcon,
  MessageSquareTextIcon,
  PlusIcon,
  UploadIcon,
} from "lucide-react";

import { useAppRouteNavigation } from "@/app/app/_/routing";
import { DialogLayer } from "@/components/organisms/Dialog";
import { Navigation, Queries, Values } from "@/constants";
import type { SidebarRoutingItem } from "@/constants/navigation";
import { useBackendQuery } from "@/hooks/useBackendQuery";
import { useLayoutColors } from "@/hooks/useLayoutColors";
import { State } from "@/state";
import { AppViewsType } from "@/state/@types";
import { Button } from "@/components/ui/button";
import {
  Item,
  ItemContent,
  ItemDescription,
  ItemGroup,
  ItemMedia,
  ItemTitle,
} from "@/components/ui/item";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Typography } from "@/components/ui/typography";
import { cn } from "@/components/ui/utils";

/**
 * The sidebar's places the tab bar carries itself, in its order; More holds
 * the rest. Budget takes Reports' place once the business has a budget, and
 * Reports is under More then.
 */
export const APP_SIDEBAR_TAB_BAR_KEYS = ["triage", "thread", "reports"];

/** What the tab bar's "+" offers: each a row of its sheet. */
const NEW_OPTIONS = [
  {
    key: "chat",
    name: "New chat",
    description: "Ask, or tell it what to do",
    icon: <MessageSquareTextIcon size="1em" />,
  },
  {
    key: "photo",
    name: "Take a photo",
    description: "A receipt or a bill, with the camera",
    icon: <CameraIcon size="1em" />,
  },
  {
    key: "upload",
    name: "Upload",
    description: "Images or PDFs from the phone",
    icon: <UploadIcon size="1em" />,
  },
] as const;

/**
 * The sidebar on a phone: the few places worked in most - the sidebar's own
 * items, named and drawn as it has them - along the bottom where a thumb
 * reaches them, and the rest of the sidebar under More.
 *
 * In the middle, the one thing that is not a place: a red "+" that slides up
 * a sheet to start something - a chat, or one about a photo taken or a file
 * picked, which arrives in a new chat attached and ready for a word about it.
 *
 * A view's own action bar - triage's Hold and Book - takes its place while it
 * is up: two bars along the bottom would leave little of the page.
 */
export function AppSidebarTabBar() {
  const colors = useLayoutColors();
  const activeAppView = State((state) => state.activeAppView);
  const activeEntityIdentifiers = State(
    (state) => state.activeEntityIdentifiers,
  );
  const navigation = useAppRouteNavigation();
  // As the sidebar asks it, so the two share the one cache entry.
  const { data: triageData } = useBackendQuery(
    Queries.TRIAGE_CONNECTION_QUERY,
    {
      variables: { entityIdentifiers: activeEntityIdentifiers, first: 25 },
      fetchPolicy: "cache-first",
    },
  );
  const triageCount = triageData?.triageConnection?.data?.totalCount ?? 0;
  // The business picked at the top, as the budget view reads it.
  const entityIdentifier = State(
    (state) =>
      state.scopedEntityIdentifier ?? state.activeEntityIdentifiers?.[0],
  );
  const { data: budgetData } = useBackendQuery(Queries.BUDGET_IDS_QUERY, {
    variables: {
      entityIdentifiers: entityIdentifier ? [entityIdentifier] : [],
    },
    skip: !entityIdentifier || !Values.IS_BUDGET_SHOWN,
    fetchPolicy: "cache-first",
  });
  const hasBudget = (budgetData?.budgets?.data?.length ?? 0) > 0;
  const [isNewOpen, setIsNewOpen] = useState(false);
  // The phone's own camera and file picker: opened from the tap on their row,
  // which is what lets a browser open them.
  const photoInputRef = useRef<HTMLInputElement>(null);
  const uploadInputRef = useRef<HTMLInputElement>(null);

  const [triage, threads, reportsOrBudget] = APP_SIDEBAR_TAB_BAR_KEYS.map(
    (key) => {
      const place = key === "reports" && hasBudget ? "budget" : key;
      const item = Navigation.SIDEBAR.find(
        (one): one is SidebarRoutingItem =>
          one.type === "routing" && one.key === place,
      )!;
      return {
        key: item.key,
        name: item.name,
        icon: item.icon,
        isActive: activeAppView === item.key,
        badge: item.key === "triage" ? triageCount : undefined,
        onClick: () => {
          navigation.navigateToView(item.key as AppViewsType, {
            preservePanel: true,
            preserveQuery: false,
          });
        },
      };
    },
  );
  const more = {
    key: "more",
    name: "More",
    icon: <ListIcon size="1em" />,
    isActive: false,
    badge: undefined,
    onClick: () => State.getState().setIsSidebarMinimized(false),
  };

  const pick = (key: (typeof NEW_OPTIONS)[number]["key"]) => {
    if (key === "chat") {
      setIsNewOpen(false);
      State.getState().openNewThreadTab();
      State.getState().openThreadPanel({ autofocus: true });
    } else {
      // The sheet stays until a file is chosen: cancelling the picker leaves
      // it where it was.
      (key === "photo" ? photoInputRef : uploadInputRef).current?.click();
    }
  };
  const attach = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = [...(event.target.files ?? [])];
    event.target.value = "";
    if (files.length === 0) return;
    setIsNewOpen(false);
    State.getState().openNewThreadWithFiles(files);
  };

  const tab = (place: typeof triage) => (
    <button
      key={place.key}
      type="button"
      onClick={place.onClick}
      aria-current={place.isActive ? "page" : undefined}
      data-testid={`app-tab-${place.key}`}
      className={cn(
        "relative min-h-12 [border:none] [background:none] flex flex-col items-center justify-center gap-0.5 cursor-pointer",
        place.isActive ? "text-foreground" : "text-muted-foreground/80",
      )}
    >
      <span className="flex text-[22px]">{place.icon}</span>
      <Typography level="body-xs" className="text-inherit">
        {place.name}
      </Typography>
      {place.badge ? (
        <span className="absolute top-0.5 left-[calc(50%+6px)] min-w-4.5 h-4.5 pl-1.25 pr-1.25 box-border rounded-[9px] bg-destructive text-white text-[11px] [line-height:18px] [font-variant-numeric:tabular-nums]">
          {place.badge}
        </span>
      ) : null}
    </button>
  );

  return (
    <nav
      aria-label="Main"
      data-testid="app-tab-bar"
      // Clear of the phone's home indicator.
      className="shrink-0 grid grid-cols-5 pt-1.5 pb-[calc(6px+env(safe-area-inset-bottom))] [border-top-width:1px] [border-top-style:solid] border-border bg-background [body:has([data-testid='app-layout-footer'])_&]:hidden"
    >
      {tab(triage)}
      {tab(threads)}
      <div className="flex items-center justify-center">
        <Button
          variant="destructive"
          size="icon-lg"
          aria-label="New"
          aria-haspopup="dialog"
          aria-expanded={isNewOpen}
          data-testid="app-tab-new"
          onClick={() => setIsNewOpen(true)}
          className="rounded-full"
        >
          <PlusIcon />
        </Button>
      </div>
      {tab(reportsOrBudget)}
      {tab(more)}

      {/* Not modal, as More's sheet is not: what it opens takes the
          keyboard and a press. */}
      <Sheet
        modal={false}
        open={isNewOpen}
        onOpenChange={(open) => !open && setIsNewOpen(false)}
      >
        {isNewOpen && (
          <div aria-hidden className="fixed inset-0 z-50 bg-black/50" />
        )}
        <SheetContent
          side="bottom"
          onInteractOutside={(event) => {
            if ((event.target as Element | null)?.closest("[data-popper]")) {
              event.preventDefault();
            }
          }}
          className="h-auto rounded-t-lg pb-[env(safe-area-inset-bottom)]"
          style={{ backgroundColor: colors.sidebar }}
        >
          <SheetTitle className="sr-only">New</SheetTitle>
          <DialogLayer>
            <ItemGroup data-testid="app-tab-new-sheet" className="p-2">
              {NEW_OPTIONS.map((option) => (
                <Item key={option.key} asChild>
                  <button
                    type="button"
                    data-testid={`app-tab-new-${option.key}`}
                    onClick={() => pick(option.key)}
                    className="min-h-14 w-full text-left hover:bg-accent/50"
                  >
                    <ItemMedia className="text-[22px]">{option.icon}</ItemMedia>
                    <ItemContent>
                      <ItemTitle>{option.name}</ItemTitle>
                      <ItemDescription>{option.description}</ItemDescription>
                    </ItemContent>
                  </button>
                </Item>
              ))}
            </ItemGroup>
          </DialogLayer>
          <input
            ref={photoInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            hidden
            data-testid="app-tab-new-photo-input"
            onChange={attach}
          />
          <input
            ref={uploadInputRef}
            type="file"
            accept="image/*,application/pdf"
            multiple
            hidden
            data-testid="app-tab-new-upload-input"
            onChange={attach}
          />
        </SheetContent>
      </Sheet>
    </nav>
  );
}
